import { useState, useEffect, useMemo } from 'react';
import { apiGet } from '../api/client';
import { useSlowRequestWarning } from '../hooks/useSlowRequestWarning';
import Toast from './Toast';
import UnifiedTreeView from './UnifiedTreeView';

function HierarchicalVocabulary({ config }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('tree');
  const [toast, setToast] = useState({ show: false, message: '' });
  
  const showSlowWarning = useSlowRequestWarning(loading, 2000);

  const fetchData = async () => {
    setLoading(true);
    try {
      const json = await apiGet(config.endpoint);
      setData(json);
    } catch (error) {
      setToast({ show: true, message: '❌ Error loading data: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [config.endpoint]);

  const flattenNodes = (nodes, parentPath = '') => {
    const result = [];
    Object.entries(nodes).forEach(([nodeId, nodeData]) => {
      const fullPath = parentPath ? `${parentPath} → ${nodeId}` : nodeId;
      result.push({
        id: nodeId,
        path: fullPath,
        description: nodeData.description || '',
        aka: nodeData.aka || [],
        level: parentPath.split(' → ').length - 1
      });
      if (nodeData.children) {
        result.push(...flattenNodes(nodeData.children, fullPath));
      }
    });
    return result;
  };

  const flatData = useMemo(() => {
    if (!data || !data.nodes) return [];
    return flattenNodes(data.nodes);
  }, [data]);

  const filteredFlatData = useMemo(() => {
    if (!searchTerm) return flatData;
    const searchLower = searchTerm.toLowerCase();
    return flatData.filter(item =>
      item.id.toLowerCase().includes(searchLower) ||
      item.description.toLowerCase().includes(searchLower) ||
      item.aka.some(a => a.toLowerCase().includes(searchLower)) ||
      item.path.toLowerCase().includes(searchLower)
    );
  }, [flatData, searchTerm]);

  const copyFlatList = () => {
    const text = flatData.map(item => item.id).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setToast({ show: true, message: '📋 All nodes copied to clipboard!' });
    });
  };

  if (loading) {
    return (
      <div className="spinner">
        <div>⏳ Loading...</div>
        {showSlowWarning && (
          <div style={{
            marginTop: '15px',
            padding: '12px 20px',
            background: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '6px',
            color: '#92400e',
            fontSize: '13px',
            maxWidth: '500px',
            margin: '15px auto 0'
          }}>
            ⚠️ Backend may be waking up from sleep (cold start). This can take up to 20 seconds on first request.
          </div>
        )}
      </div>
    );
  }

  if (!data) {
    return <div className="empty-state"><div className="empty-state-icon">❌</div><p>No data available</p></div>;
  }

  return (
    <div>
      <div className="controls">
        <div className="controls-left">
          <span className="badge">{flatData.length} nodes total</span>
          <button onClick={fetchData}>🔄 Refresh</button>
          <button onClick={copyFlatList}>📋 Copy All Nodes</button>
        </div>
        <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
          <div className="view-toggle">
            <button 
              className={viewMode === 'tree' ? 'active' : ''} 
              onClick={() => setViewMode('tree')}
            >
              🌳 Tree View
            </button>
            <button 
              className={viewMode === 'flat' ? 'active' : ''} 
              onClick={() => setViewMode('flat')}
            >
              📋 Flat List
            </button>
          </div>
          <input
            type="text"
            placeholder="🔍 Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{width: '220px'}}
          />
        </div>
      </div>

      {viewMode === 'tree' ? (
        <div className="table-wrapper" style={{padding: '20px'}}>
          <UnifiedTreeView 
            data={data} 
            searchTerm={searchTerm}
            config={{
              getDescription: (nodeData) => nodeData.description || '',
              getMetadata: (nodeData) => (
                <>
                  {nodeData.display_name && (
                    <span className="badge-small badge-purple">{nodeData.display_name}</span>
                  )}
                  {nodeData.aka && nodeData.aka.length > 0 && nodeData.aka.slice(0, 2).map((aka, i) => (
                    <span key={i} className="badge-small badge-gray">{aka}</span>
                  ))}
                </>
              ),
              getChildren: (nodeData) => nodeData.children || null
            }}
          />
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{width: '30%'}}>ID</th>
                <th style={{width: '35%'}}>Path</th>
                <th style={{width: '35%'}}>Description</th>
              </tr>
            </thead>
            <tbody>
              {filteredFlatData.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    <span className="code">{item.id}</span>
                    {item.aka.length > 0 && item.aka.slice(0, 2).map((aka, i) => (
                      <span key={i} className="badge-small badge-gray">{aka}</span>
                    ))}
                  </td>
                  <td style={{fontSize: '12px', color: '#64748b'}}>{item.path}</td>
                  <td style={{fontSize: '13px'}}>{item.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Toast show={toast.show} message={toast.message} onClose={() => setToast({ show: false, message: '' })} />
    </div>
  );
}

export default HierarchicalVocabulary;
